import React, { useEffect, useState } from "react";
import axios from "axios";
import { Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * DishRatings — compact "★ 4.8 · 27" pill for menu cards.
 *
 * Uses the bulk summary endpoint so a menu of 20 dishes only fires ONE
 * network call. The `RatingsProvider` fetches once on mount and passes the
 * map down via React context — no `useEffect` inside every card.
 */
const RatingsContext = React.createContext({ map: {}, refresh: () => {} });

export function RatingsProvider({ lookupKeys, children }) {
  const [map, setMap] = useState({});

  const refresh = React.useCallback(async () => {
    const filtered = (lookupKeys || []).filter(Boolean);
    if (filtered.length === 0) return;
    try {
      const { data } = await axios.get(
        `${API}/dishes/reviews/summary`,
        { params: { keys: filtered.join(",") } }
      );
      setMap(data.summaries || {});
    } catch {
      setMap({});
    }
  }, [lookupKeys]);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <RatingsContext.Provider value={{ map, refresh }}>
      {children}
    </RatingsContext.Provider>
  );
}

export function useRatings() {
  return React.useContext(RatingsContext);
}

/**
 * <DishRatingPill lookup_key="mamamina_fish_plantains" />
 * Renders nothing when there are 0 reviews (avoids showing "★ 0.0 · 0"
 * which reads worse than no rating at all).
 */
export default function DishRatingPill({ lookup_key, size = "sm" }) {
  const { map } = useRatings();
  const stats = map[lookup_key];
  if (!stats || !stats.count) return null;

  const fontSize = size === "lg" ? "text-sm" : "text-[0.72rem]";
  const iconSize = size === "lg" ? 13 : 11;
  return (
    <span
      className={`inline-flex items-center gap-1 ${fontSize} text-[var(--mm-accent)] tracking-wide`}
      data-testid={`rating-${lookup_key}`}
      title={`${stats.average.toFixed(1)} out of 5 · ${stats.count} review${stats.count === 1 ? "" : "s"}`}
    >
      <Star size={iconSize} className="fill-[var(--mm-accent)]" strokeWidth={0} />
      <span className="font-medium">{stats.average.toFixed(1)}</span>
      <span className="text-[var(--mm-cream)]/45">· {stats.count}</span>
    </span>
  );
}
